import { useMemo, useState } from 'react';
import WalkInSheet from '../components/WalkInSheet';
import { Link } from 'react-router-dom';
import { useBrandLogos } from '../components/BrandMark';
import {
  Icon,
  JobCard,
  JobFlags,
  JobWhen,
  Plate,
  QuickAction,
  StageAge,
  StageBadge,
  vehicleName,
} from '../components/JobUI';
import { useAuth } from '../context/AuthContext';
import { useLocale, useT } from '../context/LocaleContext';
import { useJobs } from '../hooks/useJobs';
import { useServiceCatalog } from '../hooks/useServiceCatalog';
import { displayName, localeTag } from '../lib/format';
import { todayBucket } from '../lib/jobStage';

const LANES = ['checked_in', 'inspection', 'in_progress'];
const UPCOMING_DAYS = 7;
const SECTION_LIMIT = 6;

const COUNTERS = [
  { id: 'attention', icon: 'error', tone: 'attn' },
  { id: 'workshop', icon: 'garage', tone: 'work' },
  { id: 'waiting', icon: 'hourglass_top', tone: 'wait' },
  { id: 'arriving', icon: 'login', tone: 'plain' },
  { id: 'ready', icon: 'directions_car', tone: 'ready' },
];

export default function OverviewPage() {
  const t = useT();
  const { locale } = useLocale();
  const { profile } = useAuth();
  const { serviceLabel } = useServiceCatalog();
  const makes = useBrandLogos();
  const { jobs, loading, error, now, live, reload } = useJobs('today');
  const isMechanic = profile?.role === 'technician';
  const [mineOnly, setMineOnly] = useState(isMechanic);
  const [walkIn, setWalkIn] = useState(false);

  const buckets = useMemo(() => {
    const horizon = new Date(now.getTime() + UPCOMING_DAYS * 24 * 60 * 60 * 1000);
    const map = { attention: [], workshop: [], waiting: [], arriving: [], ready: [], done: [], upcoming: [] };
    for (const job of jobs) {
      if (mineOnly && !job.isMine) continue;
      const bucket = todayBucket(job, now);
      if (!bucket) continue;
      if (bucket === 'upcoming' && job.scheduledAt > horizon) continue;
      map[bucket].push(job);
    }
    // Longest-waiting first where it matters; arrivals in slot order.
    const bySlot = (a, b) => (a.scheduledAt?.getTime() || 0) - (b.scheduledAt?.getTime() || 0);
    Object.values(map).forEach((list) => list.sort(bySlot));
    return map;
  }, [jobs, mineOnly, now]);

  const dateLine = new Intl.DateTimeFormat(localeTag() || (locale === 'ar' ? 'ar-EG' : 'en-GB'), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(now);

  const cardProps = { makes, serviceLabel, onChanged: reload };

  return (
    <section className="today">
      <header className="page-head today-head">
        <div>
          <span className="kicker">{dateLine}</span>
          <h1>{t('today.title')}</h1>
          <p className="lede">{t('today.lede')}</p>
        </div>
        <div className="head-actions">
          {live ? <span className="live-dot">{t('live.on')}</span> : null}
          {isMechanic ? (
            <label className="toggle-chip">
              <input
                type="checkbox"
                checked={mineOnly}
                onChange={(event) => setMineOnly(event.target.checked)}
              />
              <span>{t('today.mine')}</span>
            </label>
          ) : null}
          {!isMechanic ? (
            <button type="button" className="primary" onClick={() => setWalkIn(true)}>
              {t('walkin.button')}
            </button>
          ) : null}
          <Link to="/bookings" className="ghost">
            {t('today.allJobs')}
          </Link>
        </div>
      </header>
      {walkIn ? <WalkInSheet onClose={() => setWalkIn(false)} /> : null}

      {error ? <p className="form-error">{error}</p> : null}

      <nav className="today-counters" aria-label={t('today.title')}>
        {COUNTERS.map((counter) => (
          <a key={counter.id} href={`#today-${counter.id}`} className={`today-counter tone-${counter.tone}`}>
            <Icon name={counter.icon} />
            <strong>{loading ? '–' : buckets[counter.id].length}</strong>
            <span>{t(`today.count.${counter.id}`)}</span>
          </a>
        ))}
      </nav>

      {loading ? (
        <p className="empty">{t('common.loading')}</p>
      ) : (
        <div className="today-grid">
          <div className="today-main">
            <TodaySection
              id="attention"
              tone="attn"
              icon="error"
              title={t('today.sec.attention')}
              hint={t('today.sec.attention.hint')}
              count={buckets.attention.length}
              moreTo="/bookings?view=attention"
            >
              {buckets.attention.length === 0 ? (
                <p className="today-empty ok">
                  <Icon name="check_circle" />
                  {t('today.empty.attention')}
                </p>
              ) : (
                <div className="job-card-grid">
                  {buckets.attention.slice(0, SECTION_LIMIT).map((job) => (
                    <JobCard key={job.row.id} job={job} {...cardProps} />
                  ))}
                </div>
              )}
            </TodaySection>

            <TodaySection
              id="workshop"
              tone="work"
              icon="garage"
              title={t('today.sec.workshop')}
              hint={t('today.sec.workshop.hint')}
              count={buckets.workshop.length}
            >
              {buckets.workshop.length === 0 ? (
                <p className="today-empty">{t('today.empty.workshop')}</p>
              ) : (
                <div className="lanes">
                  {LANES.map((lane) => {
                    const laneJobs = buckets.workshop.filter((job) => job.stage === lane);
                    return (
                      <div key={lane} className={`lane stage-${lane}`}>
                        <header className="lane-head">
                          <StageBadge stage={lane} />
                          <em>{laneJobs.length}</em>
                        </header>
                        {laneJobs.length === 0 ? (
                          <p className="lane-empty">{t('today.empty.lane')}</p>
                        ) : (
                          laneJobs.map((job) => (
                            <JobCard key={job.row.id} job={job} showStage={false} {...cardProps} />
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TodaySection>

            <TodaySection
              id="waiting"
              tone="wait"
              icon="hourglass_top"
              title={t('today.sec.waiting')}
              hint={t('today.sec.waiting.hint')}
              count={buckets.waiting.length}
            >
              {buckets.waiting.length === 0 ? (
                <p className="today-empty">{t('today.empty.waiting')}</p>
              ) : (
                <div className="job-card-grid">
                  {buckets.waiting.map((job) => (
                    <JobCard key={job.row.id} job={job} {...cardProps} />
                  ))}
                </div>
              )}
            </TodaySection>
          </div>

          <aside className="today-side">
            <SideList
              id="arriving"
              icon="login"
              title={t('today.sec.arriving')}
              jobs={buckets.arriving}
              empty={t('today.empty.arriving')}
              serviceLabel={serviceLabel}
              onChanged={reload}
            />
            <SideList
              id="ready"
              icon="directions_car"
              tone="ready"
              title={t('today.sec.ready')}
              jobs={buckets.ready}
              empty={t('today.empty.ready')}
              serviceLabel={serviceLabel}
              onChanged={reload}
            />
            <SideList
              id="done"
              icon="task_alt"
              title={t('today.sec.done')}
              jobs={buckets.done}
              empty={t('today.empty.done')}
              serviceLabel={serviceLabel}
              onChanged={reload}
            />
            <SideList
              id="upcoming"
              icon="event_upcoming"
              title={t('today.sec.upcoming')}
              jobs={buckets.upcoming}
              empty={t('today.empty.upcoming')}
              serviceLabel={serviceLabel}
              onChanged={reload}
              moreTo="/bookings?view=upcoming"
            />
          </aside>
        </div>
      )}
    </section>
  );
}

function TodaySection({ id, tone, icon, title, hint, count, moreTo, children }) {
  const t = useT();
  const hidden = moreTo && count > SECTION_LIMIT ? count - SECTION_LIMIT : 0;
  return (
    <section id={`today-${id}`} className={`today-section tone-${tone}`}>
      <header className="today-section-head">
        <Icon name={icon} />
        <div>
          <h2>
            {title} <em>{count}</em>
          </h2>
          {hint ? <p>{hint}</p> : null}
        </div>
      </header>
      {children}
      {hidden ? (
        <Link to={moreTo} className="today-more">
          {t('today.more', { n: hidden })}
        </Link>
      ) : null}
    </section>
  );
}

function SideList({ id, icon, tone = 'plain', title, jobs, empty, serviceLabel, moreTo, onChanged }) {
  const t = useT();
  const shown = jobs.slice(0, SECTION_LIMIT * 2);
  const hidden = jobs.length - shown.length;
  return (
    <section id={`today-${id}`} className={`side-list tone-${tone}`}>
      <header>
        <Icon name={icon} />
        <h2>{title}</h2>
        <em>{jobs.length}</em>
      </header>
      {jobs.length === 0 ? (
        <p className="today-empty">{empty}</p>
      ) : (
        <ul>
          {shown.map((job) => (
            <li key={job.row.id} className="side-row">
              <JobWhen job={job} />
              <span className="side-row-main">
                <Link to={`/bookings/${job.row.id}`} className="card-link">
                  <strong>
                    {vehicleName(job.row.vehicles, t)} <Plate value={job.row.vehicles?.plate_number} />
                  </strong>
                </Link>
                <small>
                  {displayName(job.row.profiles)} · {serviceLabel(job.row.service_type)}
                </small>
                <span className="side-row-flags">
                  <JobFlags job={job} limit={1} />
                  <StageAge job={job} />
                </span>
              </span>
              {job.next ? <QuickAction job={job} onDone={onChanged} /> : <StageBadge stage={job.stage} />}
            </li>
          ))}
        </ul>
      )}
      {hidden > 0 && moreTo ? (
        <Link to={moreTo} className="today-more">
          {t('today.more', { n: hidden })}
        </Link>
      ) : null}
    </section>
  );
}
